import { Kbd, KbdGroup } from "@repo/ui/kbd";

import { formatShortcut } from "./format-shortcut";
import { SHORTCUTS, type ShortcutId } from "./shortcuts";

interface ShortcutKbdByIdProps {
  shortcutId: ShortcutId;
  shortcut?: never;
}

interface ShortcutKbdByValueProps {
  shortcutId?: never;
  /** Raw tinykeys binding(s) — use when the shortcut isn't in the registry. */
  shortcut: string[];
}

type ShortcutKbdProps = ShortcutKbdByIdProps | ShortcutKbdByValueProps;

/**
 * Renders a keyboard shortcut as styled `Kbd` badges.
 *
 * Accepts either a registered `shortcutId` (looked up from `SHORTCUTS`)
 * or a raw `shortcut` string array in tinykeys format.
 */
export const ShortcutKbd = ({ shortcutId, shortcut }: ShortcutKbdProps) => {
  const bindings = shortcutId ? SHORTCUTS[shortcutId].shortcut : shortcut;
  const firstBinding = bindings[0];
  if (!firstBinding) return null;

  const tokens = formatShortcut(firstBinding);
  const isChord = firstBinding.includes(" ");

  if (isChord) {
    return (
      <KbdGroup>
        {tokens.flatMap((token, i) => [
          i > 0 && (
            <span key={`s-${i}`} className="text-muted-foreground/50">
              then
            </span>
          ),
          <Kbd key={i}>{token}</Kbd>,
        ])}
      </KbdGroup>
    );
  }

  return (
    <KbdGroup>
      {tokens.map((token, i) => (
        <Kbd key={i}>{token}</Kbd>
      ))}
    </KbdGroup>
  );
};
